import './order-contract.scss';
import { validate as uuidValidate } from 'uuid';
import { AddProduct } from '../add-product/add-product';
import { Fragment, PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { Typography } from '../../../components/typography/typography';
import { Button } from '../../../components/form/button';
import Icon from '../../../components/icons/icon';
import {
  CalculateFinancialSummaryRequest,
  CouponLineItem,
  CustomFeeRequest,
  CustomProductFeeRequest,
  CustomProductLineItem,
  DamageWaiverLineItem,
  DeliveryLineItem,
  DiscountLineItem,
  FeeLineItem,
  FinancialSummary,
  PickupLineItem,
  ProductFeeLineItem,
  ProductLineItem,
  SecurityDepositLineItem,
  TaxLineItem,
} from '../../../clients/quipli-service/financials/financials';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import {
  Order,
  OrderAdditionalFee,
  OrderDocumentType,
  OrderProduct,
  OrderProductAdditionalFee,
  OrderReturnStatus,
  OrderStatus,
  OrderType,
} from '../../../clients/quipli-service/order/order';
import { Configuration } from '../../../clients/quipli-service/configuration/configuration';
import { OrderTotals } from '../create/totals';
import classNames from 'classnames';
import { ShippingType, toShippingType } from '../../../clients/quipli-service/delivery/delivery';
import { Location } from '../../../clients/quipli-service/location/location';
import Dinero, { Currency } from 'dinero.js';
import { isOrderCustomInput, OrderInputItem } from '../../../clients/quipli-service/order/order-calculation';
import { debounce, round } from 'lodash';
import { Dropdown } from '../../../components/dropdown/dropdown';
import { useAdmin } from '../../../hooks/use-admin-context';
import { Constants } from '../../../constants/constants';
import { toast } from '../../../util/toasts';
import { Modal } from '../../../components/modal/modal';
import { QrScanner } from '@yudiel/react-qr-scanner';
import { convertToMoney } from '../../../util/util';
import { ProductPrice, RentalProductPrice } from '../../../clients/quipli-service/pricing/pricing';
import { useYorentClient } from '../../../clients/yorent/yorent-client';
import { OrderSection } from '../order-section/order-section';
import { CustomSecuritytDeposit } from './custom-security-deposit';
import { BufferPeriodModal } from './buffer-period-modal';
import { UserAddress } from '../../../clients/quipli-service/user/user';
import { useOrderApiV1 } from '../../../clients/quipli-service/order/order-api-v1';
import { ProductTrackingType, ProductType } from '../../../clients/quipli-service/product/product';
import { OrderContractProduct } from './order-contract-product';
import { AddLineItemModal } from '../invoices/add-line-item-modal';
import { ConvertToCycleBillingModal } from './convert-cycle-billing';
import { OrderDeliverySelection } from '../create/order-delivery-selection';
import { TaxBreakdown, TaxCode } from '../../../clients/quipli-service/tax/tax';
import { useTaxApiV1 } from '../../../clients/quipli-service/tax/tax-api-v1';
import { usePricingApiV1 } from '../../../clients/quipli-service/pricing/pricing-api-v1';
import { RentalDatesModal } from './rental-dates-modal';

export const OrderContract = ({
  order,
  location,
  canEdit,
  isEditMode,
  refresh,
  setEditMode,
  billingAddress,
  deliveryAddress,
}: PropsWithChildren<{
  order: Order;
  location: Location;
  canEdit: boolean;
  billingAddress?: UserAddress;
  deliveryAddress?: UserAddress;
  isEditMode: boolean;
  refresh: () => Promise<any>;
  setEditMode: (v: boolean) => void;
}>) => {
  const { getMultipleConfigurations, calculateFinancialSummary } = useAPI();
  const { getTaxCode, searchTaxCodes } = useTaxApiV1();
  const { updateOrder } = useOrderApiV1();
  const { assignUnitFromQRCodeLegacy, addHourReadingFromQRLegacy } = useYorentClient();
  const { listPrices } = usePricingApiV1();

  const { contextPath, hasPermissions, isSuperAdmin } = useAdmin();

  const isEditable = canEdit && isEditMode;

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [scanQrModalOpen, setScanQrModalOpen] = useState<{
    open: boolean;
    isManualEntry: boolean;
    manualEntry: string;
  }>({
    open: false,
    isManualEntry: false,
    manualEntry: '',
  });

  const [hourReadingModalOpen, setHourReadingModalOpen] = useState<{
    open: boolean;
    unitId: number;
    unitName: string;
    productId: number;
    hour: number;
  }>({ open: false, unitId: 0, unitName: '', productId: 0, hour: 0 });

  const [isAddLineItemModalOpen, setIsAddLineItemModalOpen] = useState<boolean>(false);
  const [isSecurityDepositModalOpen, setIsSecurityDepositModalOpen] = useState<boolean>(false);
  const [isBufferPeriodModalOpen, setIsBufferPeriodModalOpen] = useState<boolean>(false);
  const [isConvertToModalopen, setIsConvertToModalOpen] = useState<boolean>(false);
  const [isRentalDatesModalOpen, setIsRentalDatesModalOpen] = useState<boolean>(false);

  const [showSummary, setShowSummary] = useState<boolean>(true);
  const [configurations, setConfigurations] = useState<Configuration[]>([]);
  const [taxCode, setTaxCode] = useState<{ id?: string; name?: string; taxCodeSource?: string } | undefined>({
    id: order.taxCode,
  });

  const [showRates, setShowRates] = useState<boolean>(order.isCycleBilled);
  const [productPrices, setProductPrices] = useState<Map<string, ProductPrice[]>>(new Map());

  const [coupon, setCoupon] = useState<string>('');
  const [customTax, setCustomTax] = useState<Dinero.Dinero>();
  const [customTaxPercentage, setCustomTaxPercentage] = useState<number>();
  const [customDelivery, setCustomDelivery] = useState<Dinero.Dinero>();
  const [customPickup, setCustomPickup] = useState<Dinero.Dinero>();
  const [customDamageWaiver, setCustomDamageWaiver] = useState<Dinero.Dinero>();
  const [customFees, setCustomFees] = useState<CustomFeeRequest[]>([]);
  const [customProductFees, setCustomProductFees] = useState<CustomProductFeeRequest[]>([]);
  const [customAdditionalDiscount, setCustomAdditionalDiscount] = useState<Dinero.Dinero>();

  const [deliveryDescriptions, setDeliveryDescriptions] = useState<{ productId: number; text: string }[]>([]);
  const [selectedDelivery, setSelectedDelivery] = useState<ShippingType | null>(null);
  const [orderInputs, setOrderInputs] = useState<OrderInputItem[]>([]);
  const [prevOrderInputs, setPrevOrderInputs] = useState<OrderInputItem[]>([]);

  const [summary, setSummary] = useState<FinancialSummary>();
  const [prevSummary, setPrevSummary] = useState<FinancialSummary>();
  const [taxCodes, setTaxCodes] = useState<TaxCode[]>();
  const [taxBreakdown, setTaxBreakdown] = useState<TaxBreakdown[] | undefined>(order.taxBreakdown);

  const [editsValid, setEditsValid] = useState<boolean>(true);

  const [lockSecurityDeposits, setLockSecurityDeposits] = useState<boolean>(false);
  const [existingSecurityDeposit, setExistingSecurityDeposit] = useState<SecurityDepositLineItem>();

  const isQrCodeEnabled = configurations.find((conf) => conf.variable === 'CONF_ENABLE_QR_CODE')?.value === '1';

  const isCustomSecurityDepositEnabled =
    configurations.find((c) => c.variable === 'CONF_ORDER_PERCENTAGE_SECURITY')?.value === '1';

  const isCustomOrderRentalRatesEnabled =
    configurations.find((c) => c.variable === 'CONF_CUSTOM_ORDER_RENTAL_RATES')?.value === '1';

  const scanQRcode = useCallback(
    debounce((result) => {
      setScanQrModalOpen((prev) => ({ ...prev, open: false }));
      if (order && result.toLocaleLowerCase().includes('manager/orders/inventoryunit?qrcode=')) {
        const unitQRId = result.substring(result.lastIndexOf('=') + 1);

        assignUnitFromQRCodeLegacy(order.id || 0, unitQRId)
          .then((res) => {
            if (res.status === 1) {
              if (res.addHourReading === 1 && res.label && res.unitId) {
                res.msg && toast(<div dangerouslySetInnerHTML={{ __html: res.msg }} />, 'error');

                setHourReadingModalOpen({
                  open: true,
                  productId: res.productId,
                  unitId: res.unitId,
                  unitName: res.label,
                  hour: res.lastHourReading,
                });
              } else {
                return refresh();
              }
            } else {
              res.msg && toast(<div dangerouslySetInnerHTML={{ __html: res.msg }} />, 'error');
            }
          })
          .catch((err) => {
            console.error(err);
            toast('Error assigning units', 'error');
          })
          .finally(() => refresh());
      }
    }, 600),
    [order],
  );

  const submitOrder = async () => {
    if (!summary) {
      toast('Please add products.', 'error');
      return;
    }

    if (!selectedDelivery) {
      toast('Please select a delivery method.', 'error');
      return;
    }

    setIsLoading(true);

    try {
      const cartTotal = summary.lineItems.reduce(
        (acc, line) => (line.type === 'Product' || line.type === 'CustomProduct' ? acc.add(line.amount) : acc),
        Dinero({ amount: 0, currency: location.currency as Currency }),
      );

      const orderDelivery = summary?.lineItems.find((line) => line.type === 'Delivery') as DeliveryLineItem;
      const delivery = orderDelivery
        ? orderDelivery.amount
        : summary?.lineItems.reduce(
            (acc, line) => (line.type === 'ProductDelivery' ? acc.add(line.amount) : acc),
            Dinero({ amount: 0, currency: location.currency as Currency }),
          );
      const orderPickup = summary?.lineItems.find((line) => line.type === 'Pickup') as PickupLineItem;
      const pickup = orderPickup
        ? orderPickup.amount
        : summary?.lineItems.reduce(
            (acc, line) => (line.type === 'ProductPickup' ? acc.add(line.amount) : acc),
            Dinero({ amount: 0, currency: location.currency as Currency }),
          );
      const damageWaiver = summary?.lineItems.find((line) => line.type === 'DamageWaiver') as DamageWaiverLineItem;
      const couponDiscount =
        summary?.lineItems.find((line) => line.type === 'TagPricing' || line.type === 'Coupon')?.amount ||
        Dinero({ amount: 0, currency: location.currency as Currency });
      const tax = summary?.lineItems.find((line) => line.type === 'Tax') as TaxLineItem;
      const locationFees = (summary?.lineItems.filter((line) => line.type === 'Fee') || []) as FeeLineItem[];
      const discount =
        summary?.lineItems.find((line) => line.type === 'AdditionalDiscount')?.amount ??
        Dinero({ amount: 0, currency: location.currency as Currency });

      const orderSecurityDeposit = summary?.lineItems.find((line) => line.type === 'SecurityDeposit') as
        | SecurityDepositLineItem
        | undefined;

      const securityDepositPrice = orderSecurityDeposit
        ? orderSecurityDeposit.amount
        : summary?.lineItems.reduce(
            (acc, line) => (line.type === 'ProductSecurityDeposit' ? acc.add(line.amount) : acc),
            Dinero({ amount: 0, currency: location.currency as Currency }),
          );

      const existingProductFees = order.products.flatMap((p) => p.additionalFees);

      await updateOrder(order.id, {
        documentType: order.documentType,
        locationId: location.id,
        billingAddressId: billingAddress?.id,
        deliveryAddressId: deliveryAddress?.id,
        deliveryType: selectedDelivery,
        currency: summary.total.getCurrency(),
        cartTotal: cartTotal.getAmount(),
        damageWaiver: damageWaiver
          ? {
              amount: damageWaiver.amount.getAmount(),
              hasCustomAmount: damageWaiver.hasCustomAmount,
            }
          : undefined,
        pickup: pickup
          ? {
              amount: pickup?.getAmount() || 0,
              hasCustomAmount: orderPickup?.hasCustomAmount || false,
            }
          : undefined,
        delivery: delivery
          ? {
              amount: delivery?.getAmount() || 0,
              hasCustomAmount: orderDelivery?.hasCustomAmount || false,
            }
          : undefined,
        tax: {
          taxCode: tax.taxCodeId,
          amount: tax.amount.getAmount(),
          hasCustomAmount: tax.hasCustomAmount,
          hasCustomPercentage: tax.hasCustomPercentage,
          percentage: tax.percentage,
          breakdown: taxBreakdown,
        },
        coupon: !couponDiscount.isZero()
          ? {
              code: order.user.tagPricing?.name || coupon,
              amount: couponDiscount.multiply(-1).getAmount(),
              isVip: false,
            }
          : undefined,
        additionalFees: locationFees.map((fee) => {
          const orderFee = order.additionalFees.find((f) => f.feeId === fee.feeId);

          return {
            orderFeeId: orderFee?.id,
            feeId: fee.feeId,
            amount: fee.amount.getAmount(),
            hasCustomAmount: fee.hasCustomAmount,
          };
        }),
        additionalDiscount: !discount.isZero() ? discount.multiply(-1).getAmount() : undefined,
        total: summary.total.getAmount(),
        googleCalculation: summary.googleCalculation,
        securityDeposit: !securityDepositPrice.isZero()
          ? {
              amount: securityDepositPrice.getAmount(),
              percentage: orderSecurityDeposit?.percentage || 0,
            }
          : undefined,
        products: orderInputs.map((input) => {
          if (isOrderCustomInput(input)) {
            const customLineItem = summary.lineItems.find(
              (line) => line.type === 'CustomProduct' && line.id === input.id,
            )! as CustomProductLineItem;

            return {
              uniqueId: uuidValidate(input.id) ? '' : input.id,
              productId: input.productId,
              name: input.name,
              description: input.description,
              taxable: input.taxable,
              generalLedgerAccountId: input.generalLedgerAccountId,
              taxCategoryId: input.taxCategoryId,
              type: input.type,
              amount: customLineItem.unitPrice.getAmount(),
              hasCustomAmount: true,
              quantity: input.quantity,
              isCustom: true,
              start: input.type == OrderType.Rental ? input.start : undefined,
              end: input.type == OrderType.Rental && input.end ? input.end : undefined,
              customOrderRentalRates: input.type == OrderType.Rental ? input.customOrderRentalRates : undefined,
              equipmentOwner: input.equipmentOwner,
            };
          }

          const productLineItem = summary.lineItems.find(
            (line) => line.type === 'Product' && line.id === input.id,
          )! as ProductLineItem;

          return {
            uniqueId: uuidValidate(input.id) ? '' : input.id,
            productId: input.productId,
            type: input.type,
            amount: productLineItem.unitPrice.getAmount(),
            hasCustomAmount: productLineItem.hasCustomAmount,
            pickup:
              summary.lineItems
                .find((line) => line.type === 'ProductPickup' && line.productLineItemId === input.id)
                ?.amount?.getAmount() || 0,
            delivery:
              summary.lineItems
                .find((line) => line.type === 'ProductDelivery' && line.productLineItemId === input.id)
                ?.amount?.getAmount() || 0,
            securityDeposit: summary.lineItems
              .find((line) => line.type === 'ProductSecurityDeposit' && line.productLineItemId === input.id)
              ?.amount?.getAmount(),
            quantity: input.quantity,
            additionalFees: summary.lineItems
              .filter((line) => line.type === 'ProductFee' && line.productLineItemId === input.id)
              .map((line) => {
                const fee = line as ProductFeeLineItem;
                const orderProductFee = existingProductFees.find(
                  (f) => f.attributeId === fee.productFeeId && `${f.orderProductId}` === input.id,
                );

                return {
                  orderFeeId: orderProductFee?.id,
                  feeId: fee.productFeeId,
                  amount: fee.amount.getAmount(),
                  hasCustomAmount: fee.hasCustomAmount,
                };
              }),
            discount: input.manualDiscount,
            start: input.type == OrderType.Rental ? input.start : undefined,
            end: input.type == OrderType.Rental && input.end ? input.end : undefined,
            customOrderRentalRates:
              input.type == OrderType.Rental
                ? input.customOrderRentalRates?.filter(
                    (rr) => rr.orderId || rr.orderProductId || rr.action !== 'remove',
                  )
                : undefined,
            taxCode: { ...taxCode, id: taxCode?.id !== 'taxJar' ? taxCode?.id : undefined },
            options: [],
            services: [],
            isCustom: false,
          };
        }),
      });
      await refresh();

      setEditMode(false);
      toast('Order updated successfully.');
    } catch (err: any) {
      toast(err.msg || err.message || 'Error submitting order', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const getFinancialRequest = (): CalculateFinancialSummaryRequest => {
    return {
      customerId: order.user.id,
      locationId: location.id,
      customDelivery: customDelivery?.getAmount(),
      customPickup: customPickup?.getAmount(),
      customTax: customTax?.getAmount(),
      customTaxPercentage: customTaxPercentage,
      customDamageWaiver: customDamageWaiver?.getAmount(),
      customFees: customFees,
      customProductFees: customProductFees,
      couponCode: coupon,
      additionalDiscount: customAdditionalDiscount?.getAmount(),
      billingAddressId: billingAddress?.id,
      deliveryAddressId: deliveryAddress?.id,
      deliveryType: selectedDelivery || undefined,
      paymentMethod: order.paymentMethodType,
      isCycleBilled: order.isCycleBilled,
      cycleLength: order.cycleBillingTerms?.cycleLength,
      items: order.isCycleBilled
        ? orderInputs.map((oi) => {
            if (oi.type === OrderType.Rental) {
              return {
                ...oi,
                end: oi.start.plus(order.cycleBillingTerms!.cycleLength),
              };
            } else {
              return oi;
            }
          })
        : orderInputs,
      taxRateId: taxCode?.id,
      isTaxJar: taxCode?.taxCodeSource?.toLowerCase() === 'taxjar',
      securityDeposit: { amount: order.rentalSecurity, percentage: order.rentalSecurityPercentage },
      documentType: order.documentType,
    };
  };

  // Call Only when editMode is true
  const calculateSummary = useCallback(
    debounce(async (request: CalculateFinancialSummaryRequest) => {
      setIsLoading(true);

      try {
        const response = await calculateFinancialSummary(request);

        if (order.status === OrderStatus.Approved && lockSecurityDeposits && existingSecurityDeposit) {
          // If security deposits are locked, replace the newly calculated security deposit with the locked one
          const nonSecurityDepositLineItems = response.lineItems.filter(
            (l) => ['ProductSecurityDeposit', 'SecurityDeposit'].indexOf(l.type) < 0,
          );
          response.lineItems = [...nonSecurityDepositLineItems, existingSecurityDeposit];
        }

        setSummary(response);
        if (response.error) {
          toast(response.error, 'error');
        }
      } catch (ex: any) {
        toast(ex.msg || 'Error calculating financial summary.', 'error');
      } finally {
        setIsLoading(false);
      }
    }, 300),
    [order, lockSecurityDeposits, existingSecurityDeposit],
  );

  // Call Only at the time of loading
  const calculateOnLoad = useCallback(
    debounce(async () => {
      setInitStatesValues();

      const currency = location.currency as Currency;
      // Get available delivery options for each product and product prices
      const prodPrices: Map<string, ProductPrice[]> = new Map();

      for (const op of order.products) {
        const _prices = await listPrices([op.product.id], location.id, order.id, op.id);

        const pricesWithOriginals = (_prices as RentalProductPrice[]).map((price) => ({
          ...price,
          originalPrice:
            price.type === OrderType.Rental && price.orderId
              ? (_prices.find(
                  (p) =>
                    p.type === OrderType.Rental &&
                    !p.orderId &&
                    p.durationValue === price.durationValue &&
                    p.durationUnit === price.durationUnit,
                ) as RentalProductPrice)
              : undefined,
        }));
        const overriddenIds = pricesWithOriginals.map((p) => p.originalPrice?.id).filter((id) => !!id) as string[];

        prodPrices.set(
          `${op.id}`,
          pricesWithOriginals
            .filter((p) => overriddenIds.indexOf(p.id) < 0 && (p.orderProductId == op.id || !p.orderProductId))
            .sort((a, b) => a.durationMinutes - b.durationMinutes),
        );
      }

      setProductPrices(prodPrices);
      // Set order inputs
      const inputs = order.products.map((op) => {
        return {
          // Request props
          id: `${op.id}`,
          type: op.type,
          productId: op.product.id,
          customPrice: convertToMoney(op.customerBuyingPrice, currency).getAmount(),
          quantity: op.quantity,
          manualDiscount: op.discountPercentage,
          deliveryType: toShippingType(op.shippingTypeId),
          customOrderRentalRates:
            op.type === OrderType.Rental ? (prodPrices.get(`${op.id}`) as RentalProductPrice[] | undefined) : undefined,
          // rentalPriceRates,

          ...(op.type === OrderType.Rental && { start: op.rentalStartDate, end: op.rentalEndDate }),
          ...(op.product.type === ProductType.Custom && {
            name: op.name,
            description: op.description,
            taxable: !op.isTaxFree,
            generalLedgerAccountId: op.generalLedgerAccountId,
            taxCategoryId: op.taxCategoryId,
            isCustom: true,
          }),

          // Calculation props
          // deliveryOptions: deliveryOptions.get(op.product.id) || [],
          assignedUnits: op.assignedUnits.map((unit) => unit.id),
        } as OrderInputItem;
      });
      setOrderInputs(inputs);
      setPrevOrderInputs(inputs);

      // Set initial summary
      const productFeeLineItems: ProductFeeLineItem[] = [];

      const productLineItems: ProductLineItem[] = order.products.map((product) => {
        const additionalFees = product.additionalFees.map(
          (fee: OrderProductAdditionalFee) =>
            ({
              productLineItemId: `${product.id}`,
              productFeeId: +fee.id,
              productId: +fee.productId,
              description: fee.feeTitle,
              type: 'ProductFee',
              percentage: fee.feeType === 'P' ? fee.feeValue || 0 : 0,
              taxable: fee.taxApplied,
              unitPrice: convertToMoney(+fee.feeAppliedValue, currency),
              amount: convertToMoney(+fee.feeAppliedValue, currency),
              recurring: fee.recurring,
              hasCustomAmount: !!fee.updatedAt,
            } as ProductFeeLineItem),
        );

        productFeeLineItems.push(...additionalFees);

        return {
          id: `${product.id}`,
          productId: +product.product.id,
          type: 'Product',
          description: product.name,
          transactionType: product.type,
          unitPrice: convertToMoney(+product.customerBuyingPrice || 0, currency),
          quantity: +product.quantity,
          taxable: !product.isTaxFree,
          tax: convertToMoney(+product.tax, currency),
          amount: convertToMoney(
            +product.customerBuyingPrice *
              +product.quantity *
              (product.discountPercentage ? +(1 - product.discountPercentage / 100) : 1),
            currency,
          ),
          hasCustomAmount: false,
          taxCategoryId: product.taxCategoryId,
        } as ProductLineItem;
      });

      const feeLineItems: FeeLineItem[] = order.additionalFees.map(
        (fee: OrderAdditionalFee) =>
          ({
            id: fee.id,
            feeId: +fee.feeId,
            description: fee.feeTitle,
            type: 'Fee',
            percentage: fee.feeType === 'P' ? +fee.feeValue || 0 : 0,
            taxable: fee.taxApplied,
            optional: fee.optional,
            isSelected: true,
            unitPrice: convertToMoney(+fee.feeAppliedValue, currency),
            amount: convertToMoney(+fee.feeAppliedValue, currency),
            hasCustomAmount: !!fee.updatedAt,
            quantity: 1,
            recurring: fee.recurring,
          } as FeeLineItem),
      );

      const orderDamageWaiver: DamageWaiverLineItem = {
        type: 'DamageWaiver',
        hasCustomAmount: !!order.damageWaiverUpdatedAt,
        description: 'Damage Waiver',
        quantity: 1,
        taxable: order.applyDamageWaiverTax,
        unitPrice: convertToMoney(order.damageWaiver || 0, currency),
        amount: convertToMoney(order.damageWaiver || 0, currency),
        recurring: order.damageWaiverRecurring,
        optional: false,
        isSelected: true,
      };

      const orderAdditionalDiscount: DiscountLineItem = {
        type: 'AdditionalDiscount',
        description: 'Additional Discount',
        quantity: 1,
        taxable: false,
        unitPrice: convertToMoney(-(order.additionalDiscount || 0), currency),
        amount: convertToMoney(-(order.additionalDiscount || 0), currency),
        recurring: false,
      };

      const deliveryFee: DeliveryLineItem = {
        type: 'Delivery',
        hasCustomAmount: !!order.shippingUpdatedAt,
        description: 'Delivery',
        quantity: 1,
        taxable: false,
        unitPrice: convertToMoney(order.shippingCharged || 0, currency),
        amount: convertToMoney(order.shippingCharged || 0, currency),
        recurring: false,
      };

      const pickupFee: PickupLineItem = {
        type: 'Pickup',
        hasCustomAmount: !!order.pickupUpdatedAt,
        description: 'Pickup',
        quantity: 1,
        taxable: false,
        unitPrice: convertToMoney(order.pickupCharged, currency),
        amount: convertToMoney(order.pickupCharged, currency),
        recurring: false,
      };

      let taxRate: TaxCode | undefined;
      if (order.taxCode) {
        taxRate = await getTaxCode(order.taxCode);
        setTaxCode({ id: order.taxCode, name: taxRate.name, taxCodeSource: taxRate.source });
      }

      const taxFee: TaxLineItem = {
        type: 'Tax',
        hasCustomAmount: !!order.taxUpdatedAt,
        hasCustomPercentage: !!order.taxPercentageUpdatedAt,
        taxBreakdown: order.taxBreakdown,
        taxCodeId: order.taxCode,
        description: 'Tax',
        quantity: 1,
        taxable: false,
        userIsExempted: !order.user.chargeTax,
        percentage: round((order.taxPercentage || 0) * 100, 2),
        unitPrice: convertToMoney(+order.taxCharged, currency),
        amount: convertToMoney(+order.taxCharged, currency),
        recurring: false,
      };

      const couponDiscount: CouponLineItem = {
        type: 'Coupon',
        description: 'Coupon',
        quantity: 1,
        taxable: true,
        unitPrice: convertToMoney(-(order.discountTotal || 0), currency),
        amount: convertToMoney(-(order.discountTotal || 0), currency),
        code: order.discountCoupon || '-NA-',
        productIds: [],
        recurring: false,
        isVip: false,
      };

      const securityDeposit: SecurityDepositLineItem = {
        type: 'SecurityDeposit',
        description: '',
        quantity: 1,
        taxable: false,
        unitPrice: convertToMoney(order.rentalSecurity || 0, currency),
        amount: convertToMoney(order.rentalSecurity || 0, currency),
        percentage: order.rentalSecurityPercentage,
        recurring: false,
      };

      setExistingSecurityDeposit(securityDeposit);

      const newSummary: FinancialSummary = {
        total: convertToMoney(order.netCharged, currency),
        lineItems: [
          ...productLineItems,
          ...productFeeLineItems,
          ...feeLineItems,
          orderDamageWaiver,
          orderAdditionalDiscount,
          pickupFee,
          deliveryFee,
          taxFee,
          couponDiscount,
          securityDeposit,
        ],
      };

      setSummary(newSummary);
      setPrevSummary(newSummary);
    }, 200),
    [
      order,
      location,
      configurations,
      coupon,
      customDelivery,
      customPickup,
      customDamageWaiver,
      customFees,
      customAdditionalDiscount,
      customTax,
    ],
  );

  useEffect(() => {
    isEditMode && calculateSummary(getFinancialRequest());
  }, [
    isEditMode,
    customDelivery,
    customPickup,
    customTaxPercentage,
    customTax,
    coupon,
    customDamageWaiver,
    customFees,
    customProductFees,
    customAdditionalDiscount,
    billingAddress,
    deliveryAddress,
    selectedDelivery,
    orderInputs,
    location,
    taxCode,
  ]);

  useEffect(() => {
    getMultipleConfigurations([
      'CONF_ENABLE_QR_CODE',
      'CONF_ORDER_PERCENTAGE_SECURITY',
      'CONF_CUSTOM_ORDER_RENTAL_RATES',
      'CONF_SITE_TAX_OPTIONS',
      'CONF_PRODUCT_IMAGES_V2',
      'CONF_PRORATE_PARTIAL_CYCLE_BILLING_INVOICES',
      'CONF_SALE_SERIAL_TRACKING',
      'CONF_LOCK_SECURITY_DEPOSITS',
    ])
      .then((response) => {
        setConfigurations(response);
        if (response.find((c) => c.variable === 'CONF_PRORATE_PARTIAL_CYCLE_BILLING_INVOICES')?.value === '1') {
          setShowRates(false);
        }
        if (response.find((c) => c.variable === 'CONF_SITE_TAX_OPTIONS')?.value === '5') {
          searchTaxCodes({ pageSize: 500 }).then((taxCodes) => setTaxCodes(taxCodes.items));
        }

        setLockSecurityDeposits(response.find((c) => c.variable === 'CONF_LOCK_SECURITY_DEPOSITS')?.value === '1');
      })
      .catch(() => setConfigurations([]));
  }, []);

  useEffect(() => {
    !isEditMode && calculateOnLoad();
  }, [order]);

  const setInitStatesValues = () => {
    const currency = location.currency as Currency;

    // Delivery
    setCustomDelivery(
      order.shippingUpdatedAt && order.shippingUpdatedAt.toISO() !== null
        ? convertToMoney(order.shippingCharged || 0, currency)
        : undefined,
    );
    setCustomPickup(
      order.pickupUpdatedAt && order.pickupUpdatedAt.toISO() !== null
        ? convertToMoney(order.pickupCharged || 0, currency)
        : undefined,
    );

    // Tax
    setCustomTaxPercentage(
      !!order.taxPercentageUpdatedAt && !!order.taxPercentage ? round(order.taxPercentage * 100, 2) : undefined,
    );
    setCustomTax(
      order.taxUpdatedAt && order.taxUpdatedAt.toISO() !== null
        ? convertToMoney(order.taxCharged, location.currency as Currency)
        : undefined,
    );

    // Coupon
    setCoupon(order.discountCoupon || '-NA-');

    // Damage Waiver
    setCustomDamageWaiver(
      order.damageWaiverUpdatedAt && order.damageWaiverUpdatedAt.toISO() !== null
        ? convertToMoney(order.damageWaiver || 0, currency)
        : undefined,
    );

    // Additional Fees
    setCustomFees(
      order.additionalFees
        .filter((fee) => fee.updatedAt && fee.updatedAt.toISO() !== null)
        .map((fee) => ({
          feeId: fee.feeId,
          amount: convertToMoney(fee.feeAppliedValue || 0, currency).getAmount(),
        })),
    );

    // Product Fees
    setCustomProductFees(
      order.products.flatMap((product) =>
        product.additionalFees
          .filter((fee) => fee.updatedAt && fee.updatedAt.toISO() !== null)
          .map((fee) => ({
            feeId: fee.attributeId,
            productId: product.product.id,
            productLineItemId: `${product.id}`,
            amount: convertToMoney(fee.feeAppliedValue || 0, currency).getAmount(),
          })),
      ),
    );

    // Additional Discount
    setCustomAdditionalDiscount(
      order.additionalDiscount && order.additionalDiscount > 0
        ? convertToMoney(order.additionalDiscount, currency)
        : undefined,
    );

    // Delivery Type
    setSelectedDelivery(order.products[0].shippingType);
  };

  const cancelChanges = async () => {
    setOrderInputs(prevOrderInputs);
    setSelectedDelivery(order.products[0].shippingType);

    setInitStatesValues();
    setSummary(prevSummary);
    setEditMode(false);
  };

  const addOrderProduct = async (newInput: OrderInputItem) => {
    if (newInput.type === OrderType.Rental) {
      const productPrice = await listPrices([newInput.productId], location.id, order.id);
      setProductPrices((prev) => {
        const rpp = productPrice as RentalProductPrice[];
        const pricesWithOriginals = rpp.map((price) => ({
          ...price,
          originalPrice:
            price.type === OrderType.Rental && price.orderId
              ? (productPrice.find(
                  (p) =>
                    p.type === OrderType.Rental &&
                    !p.orderId &&
                    p.durationValue === price.durationValue &&
                    p.durationUnit === price.durationUnit,
                ) as RentalProductPrice)
              : undefined,
        }));

        const overriddenIds = pricesWithOriginals.map((p) => p.originalPrice?.id).filter((id) => !!id) as string[];
        const newMap = new Map(prev);

        newMap.set(
          newInput.id,
          pricesWithOriginals
            .filter((p) => overriddenIds.indexOf(p.id) < 0)
            .sort((a, b) => a.durationMinutes - b.durationMinutes),
        );

        return newMap;
      });
    }

    setOrderInputs((prev) => [...prev, newInput]);
  };

  const updateProduct = useCallback((orderInputItem: OrderInputItem) => {
    setOrderInputs((prevOrderInputs) =>
      prevOrderInputs.map((line) => (line.id === orderInputItem.id ? orderInputItem : line)),
    );
  }, []);

  const deleteOrderProduct = (orderProductId: string) => {
    const filteredInputs = orderInputs.filter((input) => input.id !== orderProductId);

    if (filteredInputs.length === 0) {
      toast('At least one product is required', 'error');
      return;
    }

    setOrderInputs(filteredInputs);
  };

  const renderOrderProduct = (op?: OrderProduct, oi?: OrderInputItem) => {
    const exists = !!op;
    const id = (op?.id || oi?.id)!;
    const productId = (op?.product.id || oi?.productId)!;
    const type = (op?.type || oi?.type)!;

    return (
      <Fragment key={id}>
        <OrderContractProduct
          order={order}
          product={op}
          location={location}
          setEditsValid={setEditsValid}
          isEditing={isEditable}
          isShowingPrices={showRates}
          isCustomOrderRentalRatesEnabled={isCustomOrderRentalRatesEnabled}
          rentalPrices={
            type === OrderType.Rental && op
              ? (productPrices.get(`${op.id}`) as RentalProductPrice[] | undefined)
              : undefined
          }
          inputItem={exists ? orderInputs.find((input) => input.id === `${op.id}`) : oi}
          financialItem={
            !exists
              ? (summary?.lineItems.find(
                  (line) => (line.type === 'Product' || line.type === 'CustomProduct') && line.id === oi?.id,
                ) as ProductLineItem | CustomProductLineItem | undefined)
              : undefined
          }
          financialFees={
            (summary?.lineItems.filter((line) => line.type === 'ProductFee' && line.productLineItemId === `${id}`) ||
              []) as ProductFeeLineItem[]
          }
          onChange={updateProduct}
          onDelete={deleteOrderProduct}
          onChangeFee={(fee, newPrice) => {
            setCustomProductFees((prev) => [
              ...prev.filter(
                (custom) => !(custom.feeId === fee.productFeeId && custom.productLineItemId === fee.productLineItemId),
              ),
              {
                feeId: fee.productFeeId,
                productId: fee.productId,
                productLineItemId: fee.productLineItemId,
                amount: Math.round(newPrice * 100),
              } as CustomProductFeeRequest,
            ]);
          }}
          onResetFee={(fee) => {
            setCustomProductFees((prev) =>
              prev.filter(
                (custom) => !(custom.feeId === fee.productFeeId && custom.productLineItemId === fee.productLineItemId),
              ),
            );
          }}
          refresh={refresh}
          saleSerialTrackingEnabled={
            configurations.find((c) => c.variable === 'CONF_SALE_SERIAL_TRACKING')?.value === '1'
          }
          deliveryDescription={deliveryDescriptions.find((d) => d.productId === productId)}
        />
      </Fragment>
    );
  };

  const handleRentalDateUpdate = async () => {
    await refresh();

    setIsRentalDatesModalOpen(false);
  };

  const newProducts = orderInputs.filter((input) => uuidValidate(input.id));
  const newRentalProducts = newProducts.filter((product) => product.type === OrderType.Rental);
  const newSaleProducts = newProducts.filter((product) => product.type === OrderType.Sale);

  const existingRentalProducts = order.products.filter((product) => product.type === OrderType.Rental);
  const existingSaleProducts = order.products.filter((product) => product.type === OrderType.Sale);

  return (
    <>
      <div className={classNames('order-contract-summary', { hidden: !isEditable })}>
        <AddProduct
          orderId={order.id}
          locationId={location.id}
          newOrderInput={addOrderProduct}
          isCycleBilled={order.isCycleBilled === true}
          cycleLength={order.cycleBillingTerms?.cycleLength}
          openOnStart={false}
        />
      </div>

      <Modal
        title="Scan Unit QR"
        isOpen={scanQrModalOpen.open}
        close={() => setScanQrModalOpen((prev) => ({ ...prev, open: false }))}
      >
        {scanQrModalOpen.isManualEntry ? (
          <div className="bg-white p-5 md:w-[500px] flex flex-col items-center">
            <div className="min-w-full h-[44px] p-[10px] flex items-center gap-3">
              <span className="quipli-v2 text-md">QR Code:</span>
              <input
                type="text"
                className="quipli-v2 text-md"
                value={scanQrModalOpen.manualEntry}
                onChange={(e) => setScanQrModalOpen({ ...scanQrModalOpen, manualEntry: e.target.value })}
              />
            </div>
            <Button
              className="button button-primary mb-3"
              onClick={() => {
                scanQRcode('manager/orders/inventoryunit?qrcode=' + scanQrModalOpen.manualEntry);
              }}
            >
              Submit
            </Button>

            <Button
              className="button button-secondary"
              onClick={() => {
                setScanQrModalOpen((prev) => ({ ...prev, isManualEntry: false, manualEntry: '' }));
              }}
            >
              QR Scanner
            </Button>
          </div>
        ) : (
          <div className="bg-white p-5 md:w-[500px] flex flex-col items-center">
            <QrScanner
              onDecode={(result) => {
                setScanQrModalOpen((prev) => ({ ...prev, open: false }));
                scanQRcode(result);
              }}
              onError={() => {}}
            />
            <Button
              className="button button-secondary"
              onClick={() => {
                setScanQrModalOpen((prev) => ({ ...prev, isManualEntry: true }));
              }}
            >
              Enter Manually
            </Button>
          </div>
        )}
      </Modal>

      <Modal
        title="Scan QR Code"
        isOpen={hourReadingModalOpen.open}
        close={() => {
          setHourReadingModalOpen({ open: false, productId: 0, unitId: 0, unitName: '', hour: 0 });
          return refresh();
        }}
      >
        <div className="bg-white p-5 md:w-[500px] flex flex-col items-center">
          <div className="min-w-full h-[44px] p-[10px] flex items-center gap-3">
            Unit: {hourReadingModalOpen.unitName}
          </div>
          <div className="min-w-full h-[44px] p-[10px] flex items-center gap-3">
            <span className="quipli-v2 text-md">Machine Hours:</span>
            <input
              type="text"
              className="quipli-v2 text-md"
              value={hourReadingModalOpen.hour}
              onChange={(e) => setHourReadingModalOpen({ ...hourReadingModalOpen, hour: +e.target.value })}
            />
          </div>
          <Button
            className="button button-primary"
            onClick={() => {
              addHourReadingFromQRLegacy(
                order.id,
                hourReadingModalOpen.productId,
                hourReadingModalOpen.unitId,
                hourReadingModalOpen.hour,
              )
                .then(() => {})
                .catch(() => {})
                .finally(() => {
                  setHourReadingModalOpen({ open: false, productId: 0, unitId: 0, unitName: '', hour: 0 });
                  return refresh();
                });
            }}
          >
            Submit
          </Button>
        </div>
      </Modal>

      {isAddLineItemModalOpen && order.invoices.length && (
        <AddLineItemModal
          isOpen={isAddLineItemModalOpen}
          close={() => {
            setIsAddLineItemModalOpen(false);
            refresh();
          }}
          invoice={order.invoices[0]}
          currency={location.currency ?? 'USD'}
        />
      )}

      {(isCustomSecurityDepositEnabled || order.rentalSecurity !== 0) && isSecurityDepositModalOpen && (
        <CustomSecuritytDeposit
          order={order}
          onSubmit={() => {
            refresh();
            setIsSecurityDepositModalOpen(false);
          }}
          onClose={() => setIsSecurityDepositModalOpen(false)}
        />
      )}

      {isBufferPeriodModalOpen && !!order && (
        <BufferPeriodModal
          order={order}
          onSubmit={() => {
            refresh();
            setIsBufferPeriodModalOpen(false);
          }}
          onClose={() => setIsBufferPeriodModalOpen(false)}
        />
      )}

      {!order.isCycleBilled && isConvertToModalopen && (
        <ConvertToCycleBillingModal
          order={order}
          onConfirm={async () => {
            await refresh();
            setIsConvertToModalOpen(false);
          }}
          onClose={() => setIsConvertToModalOpen(false)}
        />
      )}

      {isRentalDatesModalOpen && (
        <RentalDatesModal
          order={order}
          orderInputs={orderInputs}
          timezone={location.timezone}
          onUpdate={handleRentalDateUpdate}
          onClose={() => setIsRentalDatesModalOpen(false)}
        />
      )}

      <OrderSection className={classNames('min-w-full flex flex-col')}>
        <div className="min-w-full flex gap-[4px] flex-col sm:flex-row justify-between items-start sm:items-center">
          <div className="flex gap-3 items-center shrink-0">
            <Typography type="text-md" weight="medium" className="text-grey-800">
              Contract Details
            </Typography>
          </div>
          <div className="flex gap-3 items-center w-full sm:w-fit justify-start sm:justify-end">
            {canEdit && !isEditMode && (
              <Button className="button button-secondary" disabled={isLoading} onClick={() => setEditMode(true)}>
                <Typography type="text-sm" weight="semi-bold" className="text-[var(--grey-700)] hidden md:inline">
                  Update Order
                </Typography>
                <Icon icon="Edit" />
              </Button>
            )}
            <Button
              className={classNames('button !hidden md:!flex', {
                'button-secondary': !showRates,
                'button-grey': showRates,
              })}
              data-cy={`show_rates`}
              onClick={() => setShowRates(!showRates)}
            >
              <Typography type="text-sm" weight="semi-bold" className="text-[var(--grey-700)] ">
                {showRates ? 'Hide Rates' : 'Show Rates'}
              </Typography>
              <Icon icon="Money" />
            </Button>

            {isEditable ? (
              <>
                <Button
                  className="button button-secondary"
                  onClick={() => {
                    cancelChanges();
                  }}
                  disabled={isLoading}
                >
                  <Typography type="text-sm" weight="medium" className="text-[var(--grey-700)]">
                    Cancel
                  </Typography>
                </Button>
                <Button
                  className="button button-primary"
                  disabled={isLoading || !editsValid}
                  onClick={() => {
                    order && submitOrder();
                  }}
                >
                  <Typography type="text-sm" weight="semi-bold" className="text-[white]">
                    Save Changes
                  </Typography>
                </Button>
              </>
            ) : (
              <>
                <div>
                  {order.products.some((op) => op.type === OrderType.Rental) &&
                    order.documentType == OrderDocumentType.Order && (
                      <Dropdown
                        placement="bottom-end"
                        trigger={() => (
                          <Button className="button button-secondary" data-cy={`download_menu`}>
                            <Typography
                              type="text-sm"
                              weight="semi-bold"
                              className="text-[var(--grey-700)] hidden md:inline"
                            >
                              Download
                            </Typography>

                            <div className="inline md:hidden">
                              <Icon icon="Download" />
                            </div>

                            <Icon icon="ChevronDown" />
                          </Button>
                        )}
                      >
                        {!order.isCycleBilled && order.invoices.length == 0 && (
                          <div
                            className="min-w-full flex justify-start items-center gap-2 px-0 hover:bg-[var(--grey-100)]"
                            onClick={() => {
                              window.open(`${contextPath}legacy-app/manager/orders/invoice?orders=${order.id}`);
                            }}
                          >
                            <Typography
                              className="flex justify-center justify-items-center gap-1 text-[var(--quipli-black-800)]"
                              type="text-sm"
                            >
                              Invoice
                            </Typography>
                          </div>
                        )}

                        <div
                          className="min-w-full flex justify-start items-center gap-2 px-0 hover:bg-[var(--grey-100)]"
                          onClick={() => {
                            window.open(
                              `${contextPath}legacy-app/manager/orders/contract_rental_policy/${order.id}/unsigned`,
                            );
                          }}
                        >
                          <Typography
                            className="flex justify-center justify-items-center gap-1 text-[var(--quipli-black-800)]"
                            type="text-sm"
                          >
                            Sign in person
                          </Typography>
                        </div>

                        {order.isCycleBilled || (order && order.invoices.length > 0) ? (
                          <>
                            <div
                              className="min-w-full flex justify-start items-center gap-2 px-0 hover:bg-[var(--grey-100)]"
                              onClick={() => {
                                window.open(`${contextPath}quipli-service/yorent/v2/orders/${order.id}/contract`);
                              }}
                            >
                              <Typography
                                className="flex justify-center justify-items-center gap-1 text-[var(--quipli-black-800)]"
                                type="text-sm"
                              >
                                Contract
                              </Typography>
                            </div>

                            {order.signature && (
                              <div
                                className="min-w-full flex justify-start items-center gap-2 px-0 hover:bg-[var(--grey-100)]"
                                onClick={() => {
                                  window.open(
                                    `${contextPath}legacy-app/manager/orders/contract_rental_policy/${order.id}/signed`,
                                  );
                                }}
                              >
                                <Typography
                                  className="flex justify-center justify-items-center gap-1 text-[var(--quipli-black-800)]"
                                  type="text-sm"
                                >
                                  Signed Contract
                                </Typography>
                              </div>
                            )}
                          </>
                        ) : (
                          <div
                            className="min-w-full flex justify-start items-center gap-2 px-0 hover:bg-[var(--grey-100)]"
                            onClick={() => {
                              window.open(`${contextPath}legacy-app/manager/orders/invoice/${order.id}`);
                            }}
                          >
                            <Typography
                              className="flex justify-center justify-items-center gap-1 text-[var(--quipli-black-800)]"
                              type="text-sm"
                            >
                              E-Signature
                            </Typography>
                          </div>
                        )}
                      </Dropdown>
                    )}
                </div>

                <div className="inline-flex sm:hidden grow"></div>

                {order.documentType == OrderDocumentType.Order && (
                  <div>
                    <Dropdown
                      placement="bottom-end"
                      trigger={() => (
                        <Button className="button button-tertiary">
                          <Icon icon="DotsHorizontal" />
                        </Button>
                      )}
                    >
                      <div onClick={() => setShowRates(!showRates)}>
                        <Typography type="text-sm" className="text-quipli-black-800">
                          {showRates ? 'Hide Rates' : 'Show Rates'}
                        </Typography>
                      </div>

                      {order.returnStatus !== OrderReturnStatus.Complete && (
                        <div onClick={() => setIsRentalDatesModalOpen(true)}>
                          <Typography type="text-sm" className="text-quipli-black-800">
                            Adjust All Rental Dates
                          </Typography>
                        </div>
                      )}

                      {isQrCodeEnabled &&
                        order?.products.some(
                          (op) =>
                            op.type === OrderType.Rental &&
                            op.product.trackingType === ProductTrackingType.SerialUnits &&
                            (!op.status || op.status === OrderReturnStatus.NotStarted),
                        ) && (
                          <div
                            onClick={() => {
                              setScanQrModalOpen((prev) => ({ ...prev, open: true }));
                            }}
                          >
                            <Typography type="text-sm" className="text-quipli-black-800">
                              Scan Unit
                            </Typography>
                          </div>
                        )}

                      {(isCustomSecurityDepositEnabled || order.rentalSecurity !== 0) &&
                        ![OrderStatus.Cancelled, OrderStatus.Missed].includes(order.status) && (
                          <div onClick={() => setIsSecurityDepositModalOpen(true)}>
                            <Typography type="text-sm" className="text-quipli-black-800">
                              Set Custom Security Deposit
                            </Typography>
                          </div>
                        )}

                      {(hasPermissions(Constants.Permissions.Order_Buffer_Period) || isSuperAdmin) &&
                        ![OrderStatus.Cancelled, OrderStatus.Missed].includes(order.status) && (
                          <div
                            onClick={() => {
                              setIsBufferPeriodModalOpen(true);
                            }}
                          >
                            <Typography type="text-sm" className="text-quipli-black-800">
                              Add Buffer Period
                            </Typography>
                          </div>
                        )}

                      {(hasPermissions(Constants.Permissions.Refund_Upcharge_Order) || isSuperAdmin) && (
                        <div onClick={() => setIsAddLineItemModalOpen(true)}>
                          <Typography type="text-sm" className="text-quipli-black-800">
                            Add Line Item
                          </Typography>
                        </div>
                      )}

                      {!order.isCycleBilled &&
                        !order.products
                          .filter((op) => op.type === OrderType.Rental)
                          .every((op) => op.status === OrderReturnStatus.Complete) && (
                          <div onClick={() => setIsConvertToModalOpen(true)}>
                            <Typography type="text-sm" className="text-quipli-black-800">
                              Convert to Cycle Bill
                            </Typography>
                          </div>
                        )}
                    </Dropdown>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="min-w-full mt-[5px] flex flex-col">
          {order.isCycleBilled ? (
            <>
              {existingRentalProducts.length > 0 && (
                <>
                  <div className="min-w-full flex gap-2">
                    <Typography type={'text-sm'} weight={'semi-bold'} className="text-[#1D252C]">
                      Rental Items
                    </Typography>
                  </div>

                  {existingRentalProducts.map((op) => renderOrderProduct(op))}
                  {isEditMode && newRentalProducts.map((oi) => renderOrderProduct(undefined, oi))}
                </>
              )}

              {existingSaleProducts.length > 0 && (
                <>
                  <div className="min-w-full flex gap-2">
                    <Typography type={'text-sm'} weight={'semi-bold'} className="text-[#1D252C]">
                      Sale / Misc. Items
                    </Typography>
                  </div>

                  {existingSaleProducts.map((op) => renderOrderProduct(op))}
                  {isEditMode && newSaleProducts.map((oi) => renderOrderProduct(undefined, oi))}
                </>
              )}
            </>
          ) : (
            <>
              {order.products.map((op) => renderOrderProduct(op))}
              {isEditMode && newProducts.map((oi) => renderOrderProduct(undefined, oi))}
            </>
          )}

          <div className="min-w-full flex flex-col md:flex-row gap-2">
            <div className="flex-[0_1_65%] flex flex-col gap-3">
              {isEditMode && (
                <OrderDeliverySelection
                  locationId={order.locationId}
                  productTypes={orderInputs.map((op) => ({ id: op.productId, type: op.type }))}
                  selected={selectedDelivery}
                  setSelected={setSelectedDelivery}
                  setDeliveryDescriptions={setDeliveryDescriptions}
                  placeholder="Delivery"
                />
              )}

              {order.isCycleBilled && (
                <Typography type="text-xs" weight="regular" className="text-[black] w-[calc(100% - 300px)]">
                  * All one time fees including any sale items will be part of 1st invoice. Any fee with unit ‘EA’
                  (each) will also be part of the recurring invoices.
                </Typography>
              )}
            </div>
            <div className="flex-[0_1_35%]">
              <div
                className="bg-[var(--grey-800)] p-[8px] h-[38px] w-[100%] rounded-[9px] flex justify-between items-center"
                onClick={() => setShowSummary(!showSummary)}
              >
                <Typography type="text-sm" weight="medium" className="text-[white]">
                  {order.isCycleBilled ? 'Taxes & Fees' : 'Order Summary'}
                </Typography>
                {showSummary ? <Icon icon="ChevronUp" stroke="white" /> : <Icon icon="ChevronDown" stroke="white" />}
              </div>
              <div
                className={classNames('w-[100%] rounded-[9px] order-contract-summary', {
                  hidden: !showSummary,
                })}
              >
                <OrderTotals
                  isCycleBilling={order.isCycleBilled}
                  isEditable={isEditable}
                  summary={summary}
                  tagPricing={order.user.tagPricing?.name}
                  coupon={coupon || '-NA-'}
                  setCoupon={setCoupon}
                  setCustomDelivery={setCustomDelivery}
                  setCustomPickup={setCustomPickup}
                  setCustomTax={setCustomTax}
                  setCustomTaxPercentage={setCustomTaxPercentage}
                  setCustomDamageWaiver={setCustomDamageWaiver}
                  setAdditionalDiscount={setCustomAdditionalDiscount}
                  setCustomFees={setCustomFees}
                  setTaxBreakdown={setTaxBreakdown}
                  taxCodeOptions={taxCodes ? { setTaxCode, taxCode, taxCodes } : undefined}
                  taxBreakdown={taxBreakdown}
                  isTaxAmountEditable={!order.isCycleBilled}
                  location={location}
                  isDelivery={selectedDelivery ? selectedDelivery !== ShippingType.SelfPickUp : false}
                />
              </div>
            </div>
          </div>
        </div>
      </OrderSection>
    </>
  );
};
